import React, { useState } from 'react';
import styled from 'styled-components';
import { Modal, ModalTitle, ModalBody, ModalFooter } from 'design-system/src/components/Modal';
import { Typography } from 'design-system/src/components/Typography/Typography';
import { Input } from '../../Input';
import Button from 'design-system/src/components/Button/Button';
import { ErrorMessage } from 'design-system/src/components/ErrorMessage/ErrorMessage';

const StyledModalBody = styled(ModalBody)`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const StyledInput = styled(Input)`
  width: 100%;
`;

export type HoldTaskModalProps = {
  /**
   * Callback when modal is closed
   */
  onClose: () => void;
  /**
   * Callback when form is successfully submitted
   */
  onSubmit: (reason: string) => void;
  /**
   * Whether the form is currently submitting
   */
  isSubmitting?: boolean;
};

/**
 * Modal for holding a task with a required reason
 */
export const HoldTaskModal: React.FC<HoldTaskModalProps> = ({ onClose, onSubmit, isSubmitting = false }) => {
  const [reason, setReason] = useState('');
  const [hasError, setHasError] = useState(false);

  const handleConfirm = () => {
    const trimmedReason = reason.trim();
    if (!trimmedReason) {
      setHasError(true);
      return;
    }

    setHasError(false);
    onSubmit(trimmedReason);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setReason(e.target.value);
    if (hasError && e.target.value.trim()) {
      setHasError(false);
    }
  };

  return (
    <Modal onClose={onClose}>
      <ModalTitle>Not ready to do this task yet?</ModalTitle>

      <StyledModalBody>
        <Typography styledAs="bodySmallDMSans" color="var(--purple-900)">
          Turn off auto-submit for this task and set reminder for later.
        </Typography>

        <StyledInput
          label="Reason"
          placeholder="e.g. Waiting for lab results, awaiting patient response, etc."
          value={reason}
          onChange={handleInputChange}
          hasError={hasError}
        />
        {hasError && <ErrorMessage>Please provide a reason for holding this task</ErrorMessage>}
      </StyledModalBody>

      <ModalFooter>
        <Button variant="tertiary" onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button onClick={handleConfirm} isLoading={isSubmitting}>
          Confirm
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default HoldTaskModal;
